import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { IMAGES, AREAS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", mode: "In Studio (Roma)", topic: AREAS[0].title, slot: "Mattina", message: "" };
const inputCls = "w-full rounded-xl border border-[#1C1E1D]/15 bg-[#FBF9F5] px-4 py-3 text-sm text-[#1C1E1D] placeholder:text-[#8A908C] focus:outline-none focus:ring-2 focus:ring-[#C85A32]/40 focus:border-[#C85A32] transition";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Richiesta inviata. Ti ricontatterò entro 24-48 ore lavorative.");
      setForm(EMPTY);
    } catch {
      toast.error("Invio non riuscito. Controlla i campi e riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-20 lg:py-32 bg-[#F3EFE6]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="07" label="Inizia il tuo percorso" />
        <div className="mt-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal delay={0.06}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] text-[#1C1E1D]">
                Vuoi capire se questo percorso <em className="text-[#C85A32] not-italic">fa per te?</em>
              </h2>
              <p className="mt-6 text-base text-[#525754] leading-relaxed max-w-md">
                Puoi contattarmi per ricevere informazioni o richiedere un primo colloquio: valuteremo insieme la tua richiesta, senza alcun obbligo di iniziare un percorso.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="mt-9 space-y-5">
              <div className="flex items-center gap-3 text-sm text-[#525754]">
                <span className="w-10 h-10 rounded-full bg-[#FBF9F5] border border-[#1C1E1D]/10 flex items-center justify-center text-[#C85A32]"><Mail size={16} /></span>
                <a href="mailto:info@veronicasciacquatori.it" className="hover:text-[#C85A32] transition-colors" data-testid="contact-email-link">info@veronicasciacquatori.it</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#525754]">
                <span className="w-10 h-10 rounded-full bg-[#FBF9F5] border border-[#1C1E1D]/10 flex items-center justify-center text-[#C85A32]"><MapPin size={16} /></span>
                Via Ostiense 104B, Roma Ostiense
              </div>
              <div className="flex items-center gap-3 text-sm text-[#525754]">
                <span className="w-10 h-10 rounded-full bg-[#FBF9F5] border border-[#1C1E1D]/10 flex items-center justify-center text-[#C85A32]"><Clock size={16} /></span>
                Ricevo su appuntamento · in studio e online
              </div>
            </Reveal>
            <Reveal delay={0.18} className="mt-10 hidden lg:block">
              <img src={IMAGES.cozyInterior} alt="Lo studio accogliente" className="rounded-2xl h-56 w-full object-cover spotlight-img" data-testid="contact-studio-image" />
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} className="bg-[#FBF9F5] border border-[#1C1E1D]/10 rounded-2xl p-8 sm:p-10 shadow-[0_20px_50px_-15px_rgba(28,30,29,0.08)]" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="booking-name" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Nome e Cognome *</label>
                  <input id="booking-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Maria Rossi" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="booking-phone" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Telefono *</label>
                  <input id="booking-phone" data-testid="booking-phone-input" required value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="booking-email" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Email *</label>
                  <input id="booking-email" type="email" data-testid="booking-email-input" required value={form.email} onChange={set("email")} placeholder="maria@esempio.it" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="booking-mode" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Modalità preferita</label>
                  <select id="booking-mode" data-testid="booking-mode-select" value={form.mode} onChange={set("mode")} className={inputCls}>
                    <option>In Studio (Roma)</option>
                    <option>Online (videochiamata)</option>
                    <option>Indifferente</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="booking-slot" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Fascia oraria</label>
                  <select id="booking-slot" data-testid="booking-slot-select" value={form.slot} onChange={set("slot")} className={inputCls}>
                    <option>Mattina</option>
                    <option>Pausa pranzo</option>
                    <option>Pomeriggio</option>
                    <option>Sera</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="booking-topic" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Motivo principale</label>
                  <select id="booking-topic" data-testid="booking-topic-select" value={form.topic} onChange={set("topic")} className={inputCls}>
                    {AREAS.map((a) => <option key={a.n}>{a.title}</option>)}
                    <option>Altro / Preferisco parlarne a voce</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="booking-message" className="block text-xs font-medium uppercase tracking-wider text-[#525754] mb-2">Messaggio (riservato)</label>
                  <textarea id="booking-message" rows={4} data-testid="booking-message-input" value={form.message} onChange={set("message")}
                            placeholder="Raccontami in poche parole cosa ti porta qui. Resta tutto coperto da segreto professionale." className={inputCls} />
                </div>
              </div>
              <button type="submit" disabled={sending} data-testid="booking-submit-button"
                      className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A32] hover:bg-[#B04B26] disabled:opacity-60 text-[#FBF9F5] font-medium px-8 py-3.5 transition-colors duration-300">
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Invio in corso..." : "Invia la richiesta"}
              </button>
              <p className="mt-4 text-xs text-[#8A908C]">Inviando accetti la Privacy Policy. I tuoi dati sono trattati secondo il GDPR.</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
